import {
  IItinerary,
  dateObjToDateStringDashedinverted,
} from "@thekilroygroup/shared-ui";
import { IPriceItinerary, IStudentOrYouthData } from "./interfaces.lowPrice";
import { addDays, differenceInDays, startOfDay } from "date-fns";
import {
  getIsYouth,
  getIsStudent,
  hasStudentOrYouth,
} from "utils/itineraryTagsUtils";
import { getCurrency } from "utils/numberUtils";

export const createArrayOfDates = (startDate: Date) => {
  const arrayOfDates = [];
  for (let i = 1; i < 8; i++) {
    arrayOfDates.push(startOfDay(addDays(startOfDay(startDate), i)));
  }

  return arrayOfDates;
};

/**
 * Empty price itinerary obj.
 * A IPriceItinerary is a condensed version of an Itinerary obj, only containing data for rendering the calendar.
 */
export const initialPriceItinerary: IPriceItinerary = {
  index: -1,
  id: "",
  departureDate: "1111-11-11",
  returnDate: "1111-11-11",
  departureDuration: 0,
  returnDuration: 0,
  departureStops: 0,
  returnStops: 0,
  departureDateDifference: 0,
  returnDateDifference: 0,
  price: -1,
  currency: "",
  isFetching: false,
  hasStudentOrYouth: false,
  studentOrYouthData: {
    travelerType: "",
    price: -1,
    id: "",
    index: -1,
    departureDuration: -1,
    returnDuration: -1,
    departureStops: -1,
    returnStops: -1,
  },
};

/**
 * Create the skeleton for the calendar.
 * The data for the matrix without any actual itinerary data.
 * @param departureDate
 * @param returnDate
 * @param searchedDepartureDate
 * @param searchedReturnDate
 * @returns
 */
export const createEmptyCheapestItineraries = (
  departureDate: Date,
  returnDate: Date,
  searchedDepartureDate: Date,
  searchedReturnDate: Date,
) => {
  const allDepartureDates = createArrayOfDates(
    startOfDay(addDays(departureDate, -3)),
  );
  const allReturnDates = createArrayOfDates(
    startOfDay(addDays(returnDate, -3)),
  );

  const emptyCheapestCombinations: IPriceItinerary[] = allDepartureDates
    .map((depDate: Date) => {
      return allReturnDates.map((retDate: Date) => {
        const isFetching = depDate <= retDate;
        return {
          ...initialPriceItinerary,
          departureDate: dateObjToDateStringDashedinverted(depDate) as string,
          returnDate: dateObjToDateStringDashedinverted(retDate) as string,
          departureDateDifference:
            differenceInDays(
              startOfDay(depDate),
              startOfDay(searchedDepartureDate),
            ) - 1,
          returnDateDifference:
            differenceInDays(
              startOfDay(retDate),
              startOfDay(searchedReturnDate),
            ) - 1,
          isFetching: isFetching,
        };
      });
    })
    .flat();
  return emptyCheapestCombinations;
};

/**
 * Iterate through all dates in the calendar matrix.
 * Pick only the cheapest dates.
 * @param data
 * @param searchedDepartureDate
 * @param searchedReturnDate
 * @param offsetDepartureDays
 * @param offsetReturnDays
 * @returns
 */
const findCheapestCombinations = (
  data: IPriceItinerary[],
  searchedDepartureDate: Date,
  searchedReturnDate: Date,
  offsetDepartureDays: number,
  offsetReturnDays: number,
) => {
  const allDepartureDates = createArrayOfDates(
    addDays(searchedDepartureDate, -3 + offsetDepartureDays),
  );
  const allReturnDates = createArrayOfDates(
    addDays(searchedReturnDate, -3 + offsetReturnDays),
  );

  const lowestPrices: IPriceItinerary[] = allDepartureDates
    .map((departureDate) => {
      const flightsForDepartureDate = data.filter(
        (item) =>
          item.departureDate ===
          dateObjToDateStringDashedinverted(departureDate),
      );
      return allReturnDates.map((returnDate) => {
        const fallbackPriceItinerary: IPriceItinerary = {
          ...initialPriceItinerary,
          departureDate: dateObjToDateStringDashedinverted(
            departureDate,
          ) as string,
          returnDate: dateObjToDateStringDashedinverted(returnDate) as string,

          departureDateDifference:
            differenceInDays(
              startOfDay(departureDate),
              startOfDay(searchedDepartureDate),
            ) - 1,
          returnDateDifference:
            differenceInDays(
              startOfDay(returnDate),
              startOfDay(searchedReturnDate),
            ) - 1,
          currency: "",
          isFetching: false,
        };

        if (
          departureDate <= addDays(new Date(), 2) ||
          returnDate <= addDays(new Date(), 2)
        ) {
          return fallbackPriceItinerary;
        }

        const flightsForDateCombination = flightsForDepartureDate.filter(
          (item) =>
            item.returnDate === dateObjToDateStringDashedinverted(returnDate),
        );
        const cheapestRegularTicket: IPriceItinerary | null =
          flightsForDateCombination.reduce(
            (min: IPriceItinerary | null, current: IPriceItinerary) => {
              if (!current.hasStudentOrYouth) {
                if (min === null || current.price < min.price) {
                  return current;
                }
              }
              return min;
            },
            null,
          );

        const cheapestYouthStudentTicket: IPriceItinerary | null =
          flightsForDateCombination.reduce(
            (min: IPriceItinerary | null, current: IPriceItinerary) => {
              if (current.hasStudentOrYouth) {
                if (min === null || current.price < min.price) {
                  return current;
                }
              }
              return min;
            },
            null,
          );

        let next: IPriceItinerary = initialPriceItinerary;

        next = { ...fallbackPriceItinerary };

        if (cheapestRegularTicket) {
          next = {
            ...next,
            currency: cheapestRegularTicket.currency,
            index: cheapestRegularTicket.index,
            id: cheapestRegularTicket.id,
            departureDuration: cheapestRegularTicket.departureDuration,
            returnDuration: cheapestRegularTicket.returnDuration,
            departureStops: cheapestRegularTicket.departureStops,
            returnStops: cheapestRegularTicket.returnStops,
            price: cheapestRegularTicket.price,
          };
        }

        if (cheapestYouthStudentTicket) {
          next = {
            ...next,
            hasStudentOrYouth: true,
            studentOrYouthData: {
              travelerType:
                cheapestYouthStudentTicket.studentOrYouthData.travelerType,
              index: cheapestYouthStudentTicket.index,
              id: cheapestYouthStudentTicket.id,
              departureDuration: cheapestYouthStudentTicket.departureDuration,
              returnDuration: cheapestYouthStudentTicket.returnDuration,
              departureStops: cheapestYouthStudentTicket.departureStops,
              returnStops: cheapestYouthStudentTicket.returnStops,
              price: cheapestYouthStudentTicket.price,
            },
          };
        }

        return next;
      });
    })
    .flat();
  return lowestPrices;
};

const getStudentOrYouthData = (itinerary: IItinerary, index: number) => {
  const travelerType: "STU" | "YTH" | "" = getIsYouth(itinerary)
    ? "YTH"
    : getIsStudent(itinerary)
      ? "STU"
      : "";

  return {
    travelerType: travelerType,
    price: itinerary.totalFareAmounts.total,
    id: itinerary.id,
    index: index,
    departureDuration: itinerary.flights[0].flightTime,
    returnDuration: itinerary.flights[1].flightTime,
    departureStops: itinerary.flights[0].flightSegments.length - 1,
    returnStops: itinerary.flights[1].flightSegments.length - 1,
  };
};

export const buildLowPriceData = (
  itineraries: IItinerary[] | [],
  searchedDepartureDate: Date,
  searchedReturnDate: Date,
  offsetDepartureDays: number,
  offsetReturnDays: number,
) => {
  const priceItineraries: IPriceItinerary[] = itineraries.map(
    (itinerary: IItinerary, index: number) => {
      const departureDate = itinerary.flights[0].departureDateTime.substring(
        0,
        10,
      );
      const returnDate = itinerary.flights[1].departureDateTime.substring(
        0,
        10,
      );

      const departureDuration = itinerary.flights[0].flightTime;
      const returnDuration = itinerary.flights[1].flightTime;
      const departureStops = itinerary.flights[0].flightSegments.length - 1;
      const returnStops = itinerary.flights[1].flightSegments.length - 1;

      const departureDateDifference = differenceInDays(
        new Date(departureDate),
        searchedDepartureDate,
      );

      const returnDateDifference = differenceInDays(
        new Date(returnDate),
        searchedReturnDate,
      );

      const studentOrYouthData: IStudentOrYouthData = getStudentOrYouthData(
        itinerary,
        index,
      );

      return {
        ...initialPriceItinerary,
        index: index,
        id: itinerary.id,
        departureDate,
        returnDate,
        departureDuration,
        returnDuration,
        departureStops,
        returnStops,
        departureDateDifference,
        returnDateDifference,
        price: itinerary.totalFareAmounts.total,
        currency: getCurrency(itinerary),
        isFetching: false,
        hasStudentOrYouth: hasStudentOrYouth(itinerary),
        studentOrYouthData,
      };
    },
  );

  const cheapestCombinations = findCheapestCombinations(
    priceItineraries,
    searchedDepartureDate,
    searchedReturnDate,
    offsetDepartureDays,
    offsetReturnDays,
  );

  const cheapestItineraries: { [key: string]: IItinerary } = {};
  cheapestCombinations.forEach((priceItinerary: IPriceItinerary) => {
    cheapestItineraries[priceItinerary.id] = itineraries[priceItinerary.index];
    if (priceItinerary.hasStudentOrYouth) {
      cheapestItineraries[priceItinerary.studentOrYouthData.id] =
        itineraries[priceItinerary.studentOrYouthData.index];
    }
  });
  return {
    cheapestCombinations,
    cheapestItineraries,
  };
};
